// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState } from 'react'
import type { CSSProperties } from 'react'
import { FileText, FileCheck2, Loader2 } from 'lucide-react'
import * as api from '../../api/client'
import { usePharmacy } from '../../auth/PharmacyContext'
import {
  claimDocumentTab, openTherapyDocument, resolveTherapyForDocument, THERAPY_DOC_LABEL, type TherapyDocKind,
} from '../carerx/therapyDocuments'

/**
 * The therapy's patient documents on the patient header — one obvious way to open them for the
 * patient you are looking at, instead of hunting for the per-lane control further down the page.
 *
 * Both are THERAPY-grained (the drug, the anchored Rx, the baseline labs, the §05–§07 protocols
 * and the consent's clause pack all hang off a therapy), so this resolves the patient's therapies
 * first, then hands off to the ONE shared opener (carerx/therapyDocuments) that every other CareRx
 * surface uses. Nothing about which artifact gets rendered is decided here.
 *
 *   one therapy   -> open it
 *   several       -> pick which one (never guess: the wrong drug on a consent or enrollment form is
 *                    a clinical document that is wrong on paper)
 *   none          -> say so plainly; there is nothing to render, and inventing a blank form would
 *                    produce an artifact that looks like a draft but is not one
 *
 * The therapy list is fetched ON CLICK, not on page load — this sits on a page that already makes
 * several calls, and most visits never ask for a PDF.
 */
export default function PatientEnrollmentPdfButton({ patientId }: { patientId: string }) {
  const { activePharmacy } = usePharmacy()
  const [busy, setBusy] = useState<TherapyDocKind | null>(null)
  const [choices, setChoices] = useState<{ kind: TherapyDocKind; therapies: api.CareRxTherapyLane[] } | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  // `tab`: claimed synchronously in the click, because resolving the therapy is an await and a
  // window.open on the far side of one is popup-blocked silently.
  const open = async (kind: TherapyDocKind, therapyId: string, tab?: Window | null) => {
    setChoices(null); setMsg(null); setBusy(kind)
    try {
      await openTherapyDocument(kind, therapyId, activePharmacy ?? undefined, tab)
    } catch (e) {
      tab?.close()
      setMsg(e instanceof Error ? e.message : `Could not build the ${kind} PDF.`)
    } finally {
      setBusy(null)
    }
  }

  const start = async (kind: TherapyDocKind) => {
    setMsg(null); setChoices(null); setBusy(kind)
    const tab = claimDocumentTab(kind)
    try {
      const r = await resolveTherapyForDocument(patientId, kind)
      if ('therapyId' in r) { await open(kind, r.therapyId, tab); return }
      // Several therapies: the tab cannot say which drug this is for, so close it and ask. The
      // follow-up click is its own gesture and can claim a fresh one.
      tab?.close()
      setChoices({ kind, therapies: r.choices })
      setBusy(null)
    } catch (e) {
      // A caller without CareRx access gets a 403 here; say that rather than failing silently
      // the way the therapy lanes deliberately do (they hide themselves; a button cannot).
      tab?.close()
      setMsg(e instanceof Error ? e.message : 'Could not read this patient’s therapies.')
      setBusy(null)
    }
  }

  const btn: CSSProperties = {
    fontSize: 12, padding: '5px 12px', borderRadius: 8, cursor: busy ? 'progress' : 'pointer',
    border: '1px solid var(--color-border)', background: 'transparent',
    color: 'var(--color-text-mute)', display: 'inline-flex', alignItems: 'center', gap: 6,
  }

  return (
    <div style={{ margin: '0 0 10px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <button onClick={() => start('enrollment')} disabled={busy !== null} style={btn}
        title="Open the draft enrollment form for this patient (watermarked; never for signature until clinical review completes)">
        {busy === 'enrollment' ? <Loader2 size={13} className="spin" aria-hidden="true" /> : <FileText size={13} aria-hidden="true" />}
        {busy === 'enrollment' ? 'Building…' : THERAPY_DOC_LABEL.enrollment}
      </button>
      {/* L-067: the pair travels together. The patient page used to offer the enrollment form alone,
          so "which documents can I see here?" still depended on the screen you stood on. */}
      <button onClick={() => start('consent')} disabled={busy !== null} style={btn}
        title="Open the draft Patient Consent for Services — generated from this pharmacy's own branding, address and licensing state">
        {busy === 'consent' ? <Loader2 size={13} className="spin" aria-hidden="true" /> : <FileCheck2 size={13} aria-hidden="true" />}
        {busy === 'consent' ? 'Building…' : THERAPY_DOC_LABEL.consent}
      </button>

      {choices && (
        <div style={{ flexBasis: '100%', marginTop: 8, padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 8,
          background: 'var(--color-surface, transparent)', maxWidth: 560 }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 6 }}>
            This patient has {choices.therapies.length} therapies — which one is the {choices.kind === 'consent' ? 'consent' : 'form'} for?
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {choices.therapies.map(t => (
              <button key={t.id} onClick={() => open(choices.kind, t.id, claimDocumentTab(choices.kind))} style={{ ...btn, cursor: 'pointer' }}>
                {t.drug_name || 'Drug not set'}
                <span style={{ color: 'var(--color-text-soft)' }}>· {t.state}</span>
              </button>
            ))}
            <button onClick={() => setChoices(null)} style={{ ...btn, cursor: 'pointer', border: 'none' }}>Cancel</button>
          </div>
        </div>
      )}

      {msg && (
        <div role="status" style={{ flexBasis: '100%', marginTop: 8, fontSize: 12, lineHeight: 1.5, color: '#92400e',
          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px', maxWidth: 560 }}>
          {msg}
        </div>
      )}
    </div>
  )
}
